import {
  getPermissionControllers,
  hasCustomerPermission,
  hasCustomerPermissionOnEachSide,
  hasBrokerPermission,
  isAccountPermissionDisabled,
  canDeposit,
  canWithdraw,
  isBroker,
  isAccountApprovalLocked,
  getKycStatusValue,
} from "./permissionHelper";
import PermissionCustomerModelController from "../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../controller/model/PermissionBrokerModelController";

const ALWAYS_VISIBLE = [
  "dashboard",
  "transaction_flow",
  "trading_accounts",
  "marketing_tools",
  "profile_information",
  "user_regulation",
  "trading_information",
  "financial_information",
  "change_pin",
  "active_devices",
  "privacy_policy",
  "terms_of_services",
  "risk_disclosure",
  "faq",
  "about_us",
];

export function getAllowedTourKeys(user) {
  const allowed = new Set(ALWAYS_VISIBLE);
  if (!user) return allowed;

  const approvalLocked = isAccountApprovalLocked(user);
  const {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  } = getPermissionControllers(user);

  const hasManageRealAccount = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__MANAGEMENT_REAL_ACCOUNT
  );
  const hasAddRealAccount = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT
  );
  const showAccountsDropdown = !approvalLocked && (hasManageRealAccount || hasAddRealAccount);

  if (showAccountsDropdown) {
    allowed.add("accounts_list");
    allowed.add("demo_accounts");
  }
  if (!approvalLocked && hasAddRealAccount) {
    allowed.add("add_account");
  }

  const canAccountDeposit =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
    );
  const canInternalTransfer =
    !approvalLocked &&
    hasCustomerPermissionOnEachSide(customerPerm, customerDevicePerm, [
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_MT5,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5,
    ]);
  const canAccountWithdraw =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
    );

  if (showAccountsDropdown && canAccountDeposit) allowed.add("account_deposit");
  if (showAccountsDropdown && canInternalTransfer) allowed.add("account_transfer");
  if (showAccountsDropdown && canAccountWithdraw) allowed.add("account_withdraw");

  const canCashIn = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_IN
  );
  const canCashOutBase = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_OUT
  );
  const kycStatusValue = getKycStatusValue(user);
  const isAccountVerificationRequired =
    kycStatusValue === 1 || kycStatusValue === -3 || kycStatusValue === -2;
  const showCashOut = canCashOutBase && !isAccountVerificationRequired;
  const canExchange = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_EXCHANGE
  );
  const canDepositByCard = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_DEPOSIT_BY_CARD
  );
  const canViewPendingRequest = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_PENDING_REQUEST
  );
  const canViewHistory = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_HISTORY
  );

  if (!approvalLocked) {
    if (canCashIn) allowed.add("cash_in");
    if (showCashOut) allowed.add("cash_out");
    if (canInternalTransfer) allowed.add("transfer");
    if (canExchange) allowed.add("exchange");
    if (canDepositByCard) allowed.add("pay_with_card");
    if (canCashIn && canViewPendingRequest) allowed.add("cash_in_requests");
    if (canCashOutBase && canViewPendingRequest) allowed.add("cash_out_requests");
    if (canViewPendingRequest) allowed.add("pending_transactions");
    if (canViewHistory) allowed.add("transactions_history");
  }

  const userIsBroker = isBroker(user);
  const canViewClient = hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_VIEW_CLIENT
  );
  const canViewClientTreeList = !isAccountPermissionDisabled(
    brokerPerm,
    PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TREE_LIST
  );
  const canSendNotification = hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_SEND_NOTIFICATION
  );
  const canDepositClient = canDeposit(brokerPerm, brokerDevicePerm);
  const canWithdrawClient = canWithdraw(brokerPerm, brokerDevicePerm);
  const canCollectTransaction =
    userIsBroker &&
    (hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_COLLECT_CASHIN
    ) ||
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_COLLECT_CASH_OUT
      ) ||
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_TRANSFER_CASHIN
      ) ||
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_TRANSFER_CASH_OUT
      ));
  const canViewBrokerTools =
    userIsBroker &&
    hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
    );
  const canViewClientsList = userIsBroker && canViewClient;
  const canViewClientsTree = userIsBroker && canViewClientTreeList;
  const showIBDashboard =
    !approvalLocked &&
    userIsBroker &&
    (canSendNotification ||
      canViewClientsList ||
      canViewClientsTree ||
      canDepositClient ||
      canWithdrawClient ||
      canCollectTransaction);

  if (showIBDashboard) {
    allowed.add("ib_dashboard");
    if (canViewClientsList) allowed.add("clients_list");
    if (canViewClientsTree) allowed.add("clients_tree");
    if (canDepositClient) allowed.add("transfer_to_client");
    if (canWithdrawClient) allowed.add("transfer_from_client");
    if (canCollectTransaction) allowed.add("collect_transaction");
    if (canSendNotification) allowed.add("send_notification");
    if (canViewBrokerTools) allowed.add("broker_tools");
  } else if (!approvalLocked && canCollectTransaction) {
    allowed.add("collect_transaction");
  }

  return allowed;
}
